import { parseExcelToAssets, ImportResult } from './excelImportHelper';
import { parseExcelToAssetsRowWise } from './excelImportHelperRowWise';
import { parseCSVToAssets } from './csvImportHelper';

export type ParseExcelImportOptions = {
  components?: any[];
};

/**
 * Detects if buffer is CSV or Excel by attempting to parse as CSV first,
 * then falling back to Excel parsers if CSV fails.
 * 
 * @param buffer File buffer to parse
 * @param fileName Optional filename to hint at format (e.g., "data.csv")
 * @returns Parsed assets or errors
 */
export const parseExcelImport = async (
  buffer: ArrayBuffer,
  fileName?: string,
  options: ParseExcelImportOptions = {}
): Promise<ImportResult> => {
  // Try to detect format by filename first
  const isCSV = fileName?.toLowerCase().endsWith('.csv');

  if (isCSV) {
    // Attempt CSV parsing for .csv files
    const csvResult = await parseCSVToAssets(buffer);
    if (csvResult.assets.length > 0 || csvResult.errors.length === 0) {
      return csvResult;
    }
    // If CSV parsing failed or returned no data, fall through to Excel parsing
  }

  // Try Excel parsers (either because not CSV or CSV failed)
  try {
    // Prefer row-wise template parser first.
    return await parseExcelToAssetsRowWise(buffer, { components: options.components });
  } catch (rowWiseErr: any) {
    // Fallback to existing multi-sheet template parser.
    const fallbackResult = await parseExcelToAssets(buffer);
    const warning = `Row-wise parser fallback used: ${rowWiseErr?.message ?? 'Format mismatch'}`;
    return {
      ...fallbackResult,
      errors: [warning, ...(fallbackResult.errors || [])],
    };
  }
};
